/**
 * Button - Reusable button component with variants
 *
 * Variants:
 * - primary: Main action buttons with hard shadow and cyan color
 * - secondary: Less prominent actions with muted styling
 * - icon: Square icon-only buttons (48x48)
 *
 * Features:
 * - Press animation (scale 0.95, 100ms down, 150ms up)
 * - 44×44px minimum touch targets per NFR-011
 * - Hard shadow effect on primary buttons
 */

import Phaser from 'phaser';
import {
  COLORS,
  COLOR_CSS,
  FONTS,
  UI_SIZES,
  DURATIONS,
} from '../config';
import { audioSystem } from '../systems/AudioSystem';
import { drawChamferedPanel } from './SceneChrome';

export type ButtonVariant = 'primary' | 'secondary' | 'icon';

export interface ButtonConfig {
  x: number;
  y: number;
  text?: string;
  icon?: string;
  variant?: ButtonVariant;
  width?: number;
  height?: number;
  onClick?: () => void;
}

export class Button extends Phaser.GameObjects.Container {
  private background: Phaser.GameObjects.Graphics;
  private shadow: Phaser.GameObjects.Graphics | null = null;
  private label: Phaser.GameObjects.Text | null = null;
  private iconSprite: Phaser.GameObjects.Image | null = null;
  private hitArea: Phaser.GameObjects.Rectangle;
  private variant: ButtonVariant;
  private buttonWidth: number;
  private buttonHeight: number;
  private isPressed: boolean = false;
  private onClick: (() => void) | undefined;
  private activePointerId: number | null = null;
  private clickHandled: boolean = false;
  private scenePointerDownBound = false;
  private isDestroyed = false;

  constructor(scene: Phaser.Scene, config: ButtonConfig) {
    super(scene, config.x, config.y);

    this.variant = config.variant || 'primary';
    this.onClick = config.onClick;

    // Determine button size based on variant
    if (this.variant === 'icon') {
      this.buttonWidth = UI_SIZES.iconButton;
      this.buttonHeight = UI_SIZES.iconButton;
    } else {
      // Text buttons - ensure minimum touch target
      this.buttonWidth = Math.max(config.width || 200, UI_SIZES.minTouchTarget);
      this.buttonHeight = Math.max(config.height || 56, UI_SIZES.minTouchTarget);
    }

    // Create button elements
    this.background = this.createBackground();
    this.add(this.background);

    if (this.variant === 'primary') {
      this.shadow = this.createShadow();
      this.addAt(this.shadow, 0); // Add behind background
    }

    if (config.text && this.variant !== 'icon') {
      this.label = this.createLabel(config.text);
      this.add(this.label);
    }

    if (config.icon) {
      this.iconSprite = this.createIcon(config.icon);
      this.add(this.iconSprite);
    }

    // Explicit hit area (transparent) to ensure reliable input
    this.hitArea = scene.add.rectangle(0, 0, this.buttonWidth, this.buttonHeight, 0x000000, 0);
    this.hitArea.setOrigin(0.5);
    this.addAt(this.hitArea, 0);
    this.setSize(this.buttonWidth, this.buttonHeight);
    this.enableHitArea();
    this.enableScenePointerDown();

    // Set up input handlers
    this.setupInputHandlers();

    // Add to scene
    scene.add.existing(this);
  }

  /**
   * Creates the button background shape
   */
  private createBackground(): Phaser.GameObjects.Graphics {
    const graphics = this.scene.add.graphics();
    const isPrimary = this.variant === 'primary';
    const cut = this.variant === 'icon' ? 7 : 10;
    const fillColor = isPrimary ? COLORS.uiButtonPrimary : COLORS.uiButtonSecondary;
    const borderColor = isPrimary ? COLORS.player : 0x3D6B82;

    drawChamferedPanel(
      graphics,
      -this.buttonWidth / 2,
      -this.buttonHeight / 2,
      this.buttonWidth,
      this.buttonHeight,
      fillColor,
      borderColor,
      0.98,
      cut
    );

    // A short illuminated rail gives every control the same tactical signature.
    graphics.fillStyle(isPrimary ? COLORS.player : 0x00D4FF, isPrimary ? 0.9 : 0.45);
    graphics.fillRect(
      -this.buttonWidth / 2 + cut + 2,
      -this.buttonHeight / 2 + 4,
      Math.max(18, this.buttonWidth * (isPrimary ? 0.34 : 0.2)),
      2
    );

    return graphics;
  }

  /**
   * Creates hard shadow for primary buttons
   */
  private createShadow(): Phaser.GameObjects.Graphics {
    const graphics = this.scene.add.graphics();
    drawChamferedPanel(
      graphics,
      -this.buttonWidth / 2,
      -this.buttonHeight / 2 + 5,
      this.buttonWidth,
      this.buttonHeight,
      0x031009,
      0x0AA63F,
      0.88,
      10
    );

    return graphics;
  }

  /**
   * Creates the text label
   */
  private createLabel(text: string): Phaser.GameObjects.Text {
    const fontSize = this.variant === 'primary' ? 20 : 17;

    return this.scene.add.text(0, 0, text.toUpperCase(), {
      fontFamily: FONTS.display,
      fontSize: `${fontSize}px`,
      color: COLOR_CSS.uiText,
      fontStyle: 'bold',
      letterSpacing: this.variant === 'primary' ? 2 : 1,
      shadow: {
        offsetX: 0,
        offsetY: 2,
        color: '#00000088',
        blur: 0,
        fill: true,
      },
    }).setOrigin(0.5);
  }

  /**
   * Creates the icon sprite
   */
  private createIcon(iconKey: string): Phaser.GameObjects.Image {
    const icon = this.scene.add.image(0, 0, iconKey);
    this.fitIconToButton(icon);
    return icon;
  }

  private fitIconToButton(icon: Phaser.GameObjects.Image): void {
    const maxIconSize = this.variant === 'icon'
      ? this.buttonWidth * 0.62
      : this.buttonHeight * 0.58;

    const safeWidth = Math.max(1, icon.width);
    const safeHeight = Math.max(1, icon.height);
    const fitScale = Math.min(maxIconSize / safeWidth, maxIconSize / safeHeight, 1);
    icon.setScale(Phaser.Math.Clamp(fitScale, 0.05, 1));
  }

  /**
   * Sets up pointer input handlers for press animation
   */
  private setupInputHandlers(): void {
    this.hitArea.on('pointerdown', this.onPointerDown, this);
    this.hitArea.on('pointerup', this.onPointerUp, this);
  }

  /**
   * Handles pointer down - starts press animation
   */
  private onPointerDown(pointer: Phaser.Input.Pointer): void {
    if (!this.isInteractable()) return;
    if (this.isPressed) return;
    this.isPressed = true;
    this.activePointerId = pointer.id;
    this.clickHandled = false;

    // Fallback: ensure we handle pointerup even if Phaser doesn't fire it on the hit area
    this.scene?.input?.on('pointerup', this.onScenePointerUp, this);

    this.scene?.tweens?.add({
      targets: this,
      scaleX: 0.95,
      scaleY: 0.95,
      duration: DURATIONS.buttonDown,
      ease: 'Power2',
    });
  }

  /**
   * Handles pointer up - completes press animation and triggers callback
   */
  private onPointerUp(): void {
    if (!this.isPressed || this.clickHandled) return;
    this.clickHandled = true;
    this.finishPress(true);
    this.detachScenePointerUp();
  }

  private onScenePointerUp(pointer: Phaser.Input.Pointer): void {
    if (!this.isPressed || this.clickHandled) return;
    if (this.activePointerId !== null && pointer.id !== this.activePointerId) return;

    const bounds = this.hitArea.getBounds();
    const isInside = bounds.contains(pointer.x, pointer.y);

    this.clickHandled = true;
    this.finishPress(isInside);
    this.detachScenePointerUp();
  }

  private finishPress(shouldClick: boolean): void {
    if (!this.isPressed) return;
    this.isPressed = false;

    if (shouldClick && this.onClick) {
      audioSystem.play('button_tap');
      this.onClick();
    }

    const tweenManagerPaused = (this.scene?.tweens as unknown as { paused?: boolean } | undefined)?.paused === true;
    this.scene?.tweens?.killTweensOf(this);

    if (tweenManagerPaused) {
      // Ensure controls visually reset even while all tweens are paused.
      this.setScale(1);
      return;
    }

    this.scene?.tweens?.add({
      targets: this,
      scaleX: 1,
      scaleY: 1,
      duration: DURATIONS.buttonUp,
      ease: shouldClick ? 'Back.easeOut' : 'Power2',
    });
  }

  private detachScenePointerUp(): void {
    this.scene?.input?.off('pointerup', this.onScenePointerUp, this);
    this.activePointerId = null;
  }

  private enableHitArea(): void {
    this.hitArea.setInteractive(
      new Phaser.Geom.Rectangle(
        -this.buttonWidth / 2,
        -this.buttonHeight / 2,
        this.buttonWidth,
        this.buttonHeight
      ),
      Phaser.Geom.Rectangle.Contains
    );
  }

  private enableScenePointerDown(): void {
    if (this.scenePointerDownBound) return;
    this.scenePointerDownBound = true;
    this.scene?.input?.on('pointerdown', this.onScenePointerDown, this);
  }

  private onScenePointerDown(pointer: Phaser.Input.Pointer): void {
    if (!this.isInteractable()) return;
    if (this.isPressed) return;

    const bounds = this.hitArea.getBounds();
    if (!bounds.contains(pointer.x, pointer.y)) return;

    this.onPointerDown(pointer);
  }

  private isInteractable(): boolean {
    return !!this.hitArea.input && this.hitArea.input.enabled && this.active;
  }

  /**
   * Updates the button text
   */
  setText(text: string): this {
    if (this.label) {
      this.label.setText(text.toUpperCase());
    }
    return this;
  }

  /**
   * Updates or assigns the button icon.
   */
  setIcon(iconKey: string): this {
    if (this.iconSprite) {
      this.iconSprite.setTexture(iconKey);
      this.fitIconToButton(this.iconSprite);
      return this;
    }

    this.iconSprite = this.createIcon(iconKey);
    this.add(this.iconSprite);
    return this;
  }

  /**
   * Enables or disables the button
   */
  setEnabled(enabled: boolean): this {
    if (enabled) {
      this.enableHitArea();
      this.enableScenePointerDown();
      this.setAlpha(1);
    } else {
      this.hitArea.disableInteractive();
      this.setAlpha(0.5);
    }
    return this;
  }

  /**
   * Cleanup
   */
  destroy(fromScene?: boolean): void {
    if (this.isDestroyed) return;
    this.isDestroyed = true;

    this.detachScenePointerUp();
    if (this.scenePointerDownBound) {
      this.scene?.input?.off('pointerdown', this.onScenePointerDown, this);
      this.scenePointerDownBound = false;
    }
    super.destroy(fromScene);
  }

  /**
   * Changes the onClick handler
   */
  setOnClick(callback: () => void): this {
    this.onClick = callback;
    return this;
  }
}
